import {
  createContext,
  useState,
  useContext,
  useEffect,
  useRef,
  useMemo,
} from "react";
import {
  patchCartItemsList,
  deleteCartItemsList,
  getCartItems,
} from "helpers/jiraya_helper";
import UserContext from "store/guest_user/userContext";
import { useGalleryStore } from "store/gallery/galleryStore";
import { useEventStore } from "@/store/event/eventStore";

const CartContext = createContext();

export const CartProvider = ({ children }) => {
  const [cartItems, setCartItems] = useState([]);
  const { user } = useContext(UserContext);
  const isInitialMount = useRef(true);
  const hasSyncedOnPageLoad = useRef(false);
  const eventKey = useGalleryStore((state) => state.eventKey);
  const { eventId } = useEventStore();
  // Load cart items from localStorage on mount
  useEffect(() => {
    const cartStorageKey = getCartStorageKey();
    const savedCartItems = localStorage.getItem(cartStorageKey);
    if (savedCartItems) {
      setCartItems(JSON.parse(savedCartItems));
    }
  }, []);

  // Sync cart from API when user returns to the page (page load)
  useEffect(() => {
    const syncCartOnPageLoad = async (eventId, eventKey) => {
      if (user?.email && !hasSyncedOnPageLoad.current && eventId && eventKey) {
        hasSyncedOnPageLoad.current = true;
        try {
          const response = await getCartItems({
            params: {
              event_id: parseInt(eventId),
              key: eventKey,
            },
          });

          if (response.ok && response.data?.image_list) {
            // Only update if API has different items than current cart
            const apiCartItems = response.data.image_list;
            const currentCartIds = cartItems.map((item) => item.id).sort();
            const apiCartIds = apiCartItems.map((item) => item.id).sort();

            // Check if carts are different
            const cartsAreDifferent =
              currentCartIds.length !== apiCartIds.length ||
              currentCartIds.some((id, index) => id !== apiCartIds[index]);

            if (cartsAreDifferent) {
              console.debug("Syncing cart from API on page load");
              setCartItems(apiCartItems);
              // Clear localStorage since we're using API data
              const cartStorageKey = getCartStorageKey();
              localStorage.removeItem(cartStorageKey);
            }
          }
        } catch (error) {
          console.error("Error syncing cart on page load:", error);
        }
      }
    };

    // Run sync after a short delay to ensure user context is loaded
    const timeoutId = setTimeout(syncCartOnPageLoad, 100);
    return () => clearTimeout(timeoutId);
  }, [user?.email, eventId, eventKey]); // Only run when user changes

  // Save cart items to localStorage whenever cart changes
  useEffect(() => {
    if (eventId && eventKey) {
      const cartStorageKey = getCartStorageKey();
      localStorage.setItem(cartStorageKey, JSON.stringify(cartItems));
    }
  }, [cartItems, eventId, eventKey]);

  // Generate event-specific localStorage key
  const getCartStorageKey = () => {
    return `cartItems_${eventId}_${eventKey}`;
  };

  // Sync localStorage cart to API when user signs in
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    if (user?.email && cartItems.length > 0) {
      const imageIds = cartItems?.map((item) => item.id);

      patchCartItemsList({
        image_id_list: imageIds,
        event_id: parseInt(eventId),
        key: eventKey,
      })
        .then((response) => {
          if (response.ok) {
            const cartStorageKey = getCartStorageKey();
            localStorage.removeItem(cartStorageKey);
          } else {
            console.error("Failed to sync cart to API:", response.message);
          }
        })
        .catch((error) => {
          console.error("Error syncing cart to API:", error);
        });
    }
  }, [user?.email]);

  // Load cart items from API when user is authenticated and localStorage is empty
  // This is now handled by the page load sync effect above
  // Keeping this for backward compatibility but it will rarely trigger
  useEffect(() => {
    if (user?.email && cartItems.length === 0 && eventId && eventKey) {
      getCartItems({
        params: {
          event_id: parseInt(eventId),
          key: eventKey,
        },
      })
        .then((response) => {
          if (response.ok && response.data) {
            setCartItems(response?.data?.image_list);
          }
        })
        .catch((error) => {
          console.error("Error loading cart items from API:", error);
        });
    }
  }, [user?.email, eventId, eventKey]);

  const addToCart = async (item) => {
    setCartItems((prevItems) => {
      // Check if item already exists in cart
      const existingItem = prevItems.find(
        (cartItem) => cartItem.id === item.id
      );

      if (existingItem) {
        const newItems = prevItems.filter(
          (cartItem) => cartItem.id !== item.id
        );

        // Update localStorage immediately
        const cartStorageKey = getCartStorageKey();
        localStorage.setItem(cartStorageKey, JSON.stringify(newItems));

        // If user is authenticated, remove from API
        if (user?.email) {
          deleteCartItemsList({
            data: {
              image_id_list: [item.id],
              event_id: parseInt(eventId),
            },
          })
            .then((response) => {
              if (response.ok) {
                console.debug("Item removed from API cart successfully");
              } else {
                console.error(
                  "Failed to remove item from API cart:",
                  response.message
                );
              }
            })
            .catch((error) => {
              console.error("Error removing item from API cart:", error);
            });
        }

        return newItems;
      }
      const newCartItems = [...prevItems, item];

      // If user is authenticated, sync to API
      if (user?.email) {
        patchCartItemsList({
          image_id_list: [item.id],
          event_id: parseInt(eventId),
          key: eventKey,
        })
          .then((response) => {
            if (response.ok) {
              console.debug("Item added to API cart successfully");
            } else {
              console.error(
                "Failed to add item to API cart:",
                response.message
              );
            }
          })
          .catch((error) => {
            console.error("Error adding item to API cart:", error);
          });
      }

      return newCartItems;
    });
  };

  const removeFromCart = async (itemId) => {
    setCartItems((prevItems) => {
      const newItems = prevItems.filter((item) => item.id !== itemId);

      // Update localStorage immediately to prevent sync on next reload
      const cartStorageKey = getCartStorageKey();
      localStorage.setItem(cartStorageKey, JSON.stringify(newItems));

      // If user is authenticated, remove from API
      if (user?.email) {
        deleteCartItemsList({
          data: {
            image_id_list: [itemId],
            event_id: parseInt(eventId),
          },
        })
          .then((response) => {
            if (!response.ok) {
              console.error(
                "Failed to remove item from API cart:",
                response.message
              );
            }
          })
          .catch((error) => {
            console.error("Error removing item from API cart:", error);
          });
      }

      return newItems;
    });
  };

  const clearCart = () => {
    setCartItems([]);
    const cartStorageKey = getCartStorageKey();
    localStorage.removeItem(cartStorageKey);

    // If user is authenticated, clear API cart
    if (user?.email) {
      // Note: You might need to implement a clear cart API endpoint
      // For now, we'll just log that we would clear the API cart

    }
  };
  const isItemInCart = (itemId) => {
    return cartItems.some((item) => item.id === itemId);
  };

  return (
    <CartContext.Provider
      value={{
        cartItems,
        addToCart,
        removeFromCart,
        clearCart,
        isItemInCart,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

// Hook to get cart calculations (subtotal, gst, total)
export const useCartCalculations = () => {
  const { cartItems } = useCart();
  const { eventData } = useEventStore();

  const calculations = useMemo(() => {
    const subtotal = cartItems.reduce((sum, item) => {
      return sum + (eventData?.per_image_price || 0) * (item.quantity || 1);
    }, 0);

    // Only add 18% GST if sell_image_currency is 1
    const gstAmount =
      eventData?.sell_image_currency === 1 ? subtotal * 0.18 : 0;
    const total = subtotal + gstAmount;

    return { subtotal, gstAmount, total };
  }, [cartItems, eventData?.per_image_price, eventData?.sell_image_currency]);

  return calculations;
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error("useCart must be used within a CartProvider");
  }
  return context;
};

export default CartContext;
